import { collection, addDoc, doc, getDoc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

// Limites de diagnostics par type d'abonnement
const LIMITS = {
  free: 1,     // 1 diagnostic gratuit (sans téléchargement)
  silver: 3,   // 3 diagnostics par mois
  gold: 7      // 7 diagnostics par mois
};

// Vérifier si l'utilisateur peut créer un nouveau diagnostic
export const canCreateDiagnostic = async (userId) => {
  try {
    // Récupérer les données de l'utilisateur
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return { canCreate: false, error: 'Utilisateur non trouvé' };
    }

    const userData = userDoc.data();
    const { subscriptionType, diagnosticsThisMonth, diagnosticsResetDate } = userData;

    // Vérifier si on doit réinitialiser le compteur (nouveau mois)
    const now = new Date();
    const resetDate = diagnosticsResetDate?.toDate() || new Date(0);
    const monthsDiff = (now.getFullYear() - resetDate.getFullYear()) * 12 + 
                       (now.getMonth() - resetDate.getMonth());

    if (monthsDiff >= 1) {
      // Réinitialiser le compteur pour le nouveau mois
      await updateDoc(doc(db, 'users', userId), {
        diagnosticsThisMonth: 0,
        diagnosticsResetDate: now
      });
      return { 
        canCreate: true, 
        remaining: LIMITS[subscriptionType] || LIMITS.free,
        subscriptionType 
      };
    }

    // Vérifier la limite
    const limit = LIMITS[subscriptionType] || LIMITS.free;
    const remaining = limit - diagnosticsThisMonth;

    if (remaining <= 0) {
      return { 
        canCreate: false, 
        error: 'Limite de diagnostics atteinte pour ce mois',
        subscriptionType,
        limit,
        used: diagnosticsThisMonth
      };
    }

    return { 
      canCreate: true, 
      remaining,
      subscriptionType 
    };
  } catch (error) {
    console.error('Erreur lors de la vérification:', error);
    return { canCreate: false, error: error.message };
  }
};

// Créer un nouveau diagnostic
export const createDiagnostic = async (userId, diagnosticData) => {
  try {
    // Vérifier si l'utilisateur peut créer un diagnostic
    const checkResult = await canCreateDiagnostic(userId);
    if (!checkResult.canCreate) {
      return { success: false, error: checkResult.error };
    }

    // Créer le diagnostic
    const diagnosticRef = await addDoc(collection(db, 'diagnostics'), {
      userId,
      createdAt: new Date(),
      ...diagnosticData
    });

    // Incrémenter le compteur de diagnostics
    const userDoc = await getDoc(doc(db, 'users', userId));
    const currentCount = userDoc.data().diagnosticsThisMonth || 0;
    
    await updateDoc(doc(db, 'users', userId), {
      diagnosticsThisMonth: currentCount + 1
    });

    return { 
      success: true, 
      diagnosticId: diagnosticRef.id,
      remaining: checkResult.remaining - 1
    };
  } catch (error) {
    console.error('Erreur lors de la création du diagnostic:', error);
    return { success: false, error: error.message };
  }
};

// Récupérer les diagnostics d'un utilisateur
export const getUserDiagnostics = async (userId) => {
  try {
    const q = query(
      collection(db, 'diagnostics'), 
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const diagnostics = [];
    
    querySnapshot.forEach((doc) => {
      diagnostics.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, diagnostics };
  } catch (error) {
    console.error('Erreur lors de la récupération des diagnostics:', error);
    return { success: false, error: error.message };
  }
};

// Récupérer un diagnostic spécifique
export const getDiagnostic = async (diagnosticId) => {
  try {
    const diagnosticDoc = await getDoc(doc(db, 'diagnostics', diagnosticId));
    
    if (diagnosticDoc.exists()) {
      return { 
        success: true, 
        diagnostic: { id: diagnosticDoc.id, ...diagnosticDoc.data() } 
      };
    } else {
      return { success: false, error: 'Diagnostic non trouvé' };
    }
  } catch (error) {
    console.error('Erreur lors de la récupération du diagnostic:', error);
    return { success: false, error: error.message };
  }
};
